import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Divider, IconButton, Skeleton, Stack, Tooltip, Typography } from "@material-ui/core";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import EditRoundedIcon from "@material-ui/icons/EditRounded";
import OpenInFullIcon from "@material-ui/icons/OpenInFull";
import PhoneRoundedIcon from "@material-ui/icons/PhoneRounded";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { usePropertyTypes } from "hooks/usePropertyTypes";
import useDebounce from "hooks/useDebounce";
import { EditContactDialog } from "components/_dashboard/contacts/modal/EditContactDialog";
import { ContactQuickActionsMenu } from "../../contacts/detail/ContactQuickActionsMenu";
import { ContactDetailTabs } from "../../contacts/detail/ContactDetailTabs";
import { preloadContactDetailViewChunks } from "../../contacts/detail/ContactDetailView";
import TimelineTab from "../../contacts/detail/tabs/TimelineTab";
import OfficeMinisitesPanel from "../../contacts/detail/tabs/matches/OfficeMinisitesPanel";
import { useContactSurface } from "../../contacts/detail/contactSurface";
import useCrmPanelContact from "./useCrmPanelContact";
import { projectContactOntoRow } from "./contactRowProjection";
import CrmStatusControl from "../status/CrmStatusControl";
import CrmHandlerBadge from "../handler/CrmHandlerBadge";
import CrmAssignHandlerDialog from "../handler/CrmAssignHandlerDialog";
import { CRM_HANDLER_ADMIN_ONLY_HINT, canAssignHandler } from "../handler/crmHandlerAccess";
import useContactHandler from "../../../../hooks/crm/useContactHandler";
import useOrgDirectory from "../../../../hooks/useOrgDirectory";
import useAuth from "../../../../hooks/useAuth";
import CrmHotToggle from "../row/CrmHotToggle";
import { contactInitials, formatContactPhone, telHref, whatsappHref } from "../crmFormat";
import { ContactTableCustomerChip } from "../../contacts/table/rows/ContactTableCustomerChip";
import { getContactCustomerTypes } from "../../../../@types/sharedSchema";
import { getFullName } from "utils/Haim/utils";
import { crmRowDisplayName } from "../crmRowName";
import type { CrmStatus, CustomerType, RealEstateContact } from "../../../../@types/sharedSchema";
import type { CrmContactRow as CrmContactRowType } from "../../../../@types/webotServer";
import type { CrmPooledRowActions } from "../crmRowAccess";
import { POOLED_BOOK_BUSY_MESSAGE, crmViewTabTitles } from "../../../../hooks/contacts/contactBookRules";

/**
 * The CRM side panel: the contact a row click opens, BESIDE the list rather than over it.
 *
 * WHY THIS EXISTS AT ALL — it is a defect fix, not only a flow improvement. A row click
 * used to mount `ContactDetailView`, whose first line of work was then
 * `useLegacyContactsBook().getAllContacts()`. That getter is what arms the one-shot bulk fetch in
 * `AppContext`, so opening one contact downloaded the caller's ENTIRE contact book
 * (25,066 documents for one real user; p99 13.8s / 2.01MB gzipped).
 *
 * THE BODY IS THE CUSTOMER PAGE'S OWN TAB STACK (design feedback 2026-08-17: "replace the
 * side panel with the content of the contact page, starting at the tabs"). It mounts
 * `ContactDetailTabs` directly — the same registry, the same 10 tabs, the same per-tab
 * badges the full page uses — and skips only `ContactDetailHeader`, because the panel
 * already has a header of its own. Nothing above it is duplicated and nothing below it is
 * re-implemented.
 *
 * AND IT IS STILL ARRAY-FREE, which was the thing to verify before making this swap.
 * `getAllContacts()` is the getter that arms the bulk fetch, and nothing mounted here
 * calls it: the getter is reachable only through `useLegacyContactsBook`, whose import is
 * restricted to the two legacy tables, and the tabs here write through `useContactWrites`,
 * which carries no getter at all. (`ContactDetailView`, behind the ⤢ escape
 * hatch, stopped calling it too in 2026-10 — it reads the live document, like this panel.)
 *
 * THE HEADER RENDERS FROM THE ROW PROJECTION, INSTANTLY, and the heavy body mounts on a
 * SETTLED id. A realtor arrowing down the list would otherwise fire, per row, one
 * contact read plus the count queries the tab strip runs on mount. With the settle
 * a fast pass down the list costs nothing at all, while the header — name, phone, and the
 * two buttons a realtor came for — is there on the same frame as the click.
 *
 * THIS COMPONENT MUST NOT BE KEYED ON `contactId` BY ITS PARENT. `useDebounce` seeds its
 * state from the current value, so a remount makes the settled id equal the live one on
 * the very first render and the throttle silently does nothing — measured: 5 rows clicked
 * 40ms apart fired per-row requests when keyed, and none when not. Staying mounted also
 * gives the first open its instant body (nothing to wait for on a cold mount) while only
 * SWITCHING between contacts is throttled, which is exactly the split we want.
 */

/** Long enough to swallow an arrow-key pass down the list, short enough to feel like a click. */
const BODY_SETTLE_MS = 180;

/** The pooled book's tab names: the "my list" line quotes the first one. */
const POOLED_TAB_TITLES = crmViewTabTitles("shared");

/**
 * ── THE HEADER'S ACTIONS, AND WHY THEY DIFFER BY PRESENTATION ──────────────────────
 *
 * There are five things a realtor can do to the contact from here — call, WhatsApp, edit,
 * open the full page, close — and only four of them fit one row of a 340px column beside a
 * name. The fifth was the request: "add an edit button; it makes the rest crowded, so on
 * desktop we can remove the call button, and on mobile put them on two rows."
 *
 * DOCKED (md and up, beside the list): one row, and `tel:` is the one that goes. A click-
 * to-dial link on a desktop browser opens a handler dialog or nothing at all, while the
 * number itself is right there to read — so it is the only action of the five that a
 * desktop cannot actually perform.
 *
 * SHEET (below md, the bottom drawer): all five survive, on two rows, because a phone CAN
 * dial and dropping the action a phone is best at would be backwards. Identity and the two
 * NAVIGATION controls (full page, close) take the first row; the three things you DO to the
 * contact take the second, as labelled 44px targets rather than five 36px icons crammed
 * against the name.
 *
 * ── AND SINCE 2026-08-25, A SIXTH: the quick-actions `+` ────────────────────────────────
 *
 * קביעת פגישה / משימה חדשה / הוספת הערה / מסמך לחתימה — the menu the FULL customer page has
 * carried all along as a contained "פעולות" button, and the one thing this panel could not
 * reach because it deliberately does not render `ContactDetailHeader`. Now one shared
 * `ContactQuickActionsMenu` with two triggers, so the two surfaces cannot drift.
 *
 * It is a thing you DO to the contact, so it obeys the split above rather than joining the
 * navigation controls: the end of the docked icon row, and the end of the SHEET's second row.
 *
 * ICON-ONLY IN BOTH, and on the sheet that is a measurement rather than a preference. The
 * second row's three buttons are `flex: 1` inside a full-width drawer: at 360px they get
 * ~107px each, of which ~63px is text once the 18px icon and the small button's padding are
 * paid, and the longest label ("וואטסאפ") needs ~49px. A fourth LABELLED button takes all
 * four to ~79px — ~35px of text — and every one of them overflows its box. A fixed 44px `+`
 * costs the three ~16px each instead (~91px, ~47px of text): clear from 375px up, ~2px short
 * on a 360px phone, which is the narrowest still in use and the only place it is tight.
 */
type Props = {
  contactId: string;
  /**
   * The list row for this contact, when it is on the page currently loaded. Header-only:
   * it is a projection, never enough for the tabs. Absent on a cold deep link, which is
   * why every field below has a fallback.
   */
  row?: CrmContactRowType | null;
  /**
   * The list this panel was opened from is the caller's OWN book (`view: "mine"`), which
   * the server scopes to `visibleOwnerIds: [userId]` — so every row it can return is the
   * caller's by construction. False on the agency tab, where most rows are a colleague's.
   *
   * It qualifies exactly one sentence: the "ייתכן שנמחק" wording in the `missing` branch at
   * the foot of the body. Defaults true, so every other caller keeps today's wording.
   */
  scopeIsOwnBook?: boolean;
  /**
   * The agency this panel is showing, when it was opened from an agency context.
   *
   * Only needed to SET the מתווך מטפל — a designation is per (org, person), so with no org
   * in hand the control is withheld rather than guessing which agency the caller meant.
   * The badge itself renders without it: `useContactHandler` unions across every agency
   * the caller belongs to.
   */
  orgId?: string | null;
  onClose: () => void;
  onOpenFull: (contactId: string) => void;
  /**
   * The open contact's document changed — apply this patch to its row in the list.
   *
   * The list comes from the Postgres mirror and the panel reads Firestore live, so any
   * edit made in here (the overview panels, the status picker, the full edit dialog) lands
   * in the document immediately and in the mirror a drain tick later. Without this the row
   * next to the open panel keeps its old name, status and budget while the panel shows the
   * new ones — indistinguishable from a save that failed.
   */
  onContactPatched?: (patch: Partial<CrmContactRowType>) => void;
  /**
   * The panel-hosted edit dialog changed the contact's PHONE — a server-side re-key.
   * Same contract as `CrmEditContactDialog.onRekeyed`: `savedRow.contactId` is the NEW
   * id, ready to merge over the list row. Without this the row beside the open panel
   * kept the dead id until the mirror drain caught up.
   */
  onContactRekeyed?: (
    oldContactId: string,
    savedRow: Partial<CrmContactRowType> & { contactId: string }
  ) => void;
  /**
   * How this panel is being presented — see the block above the type.
   *
   * From the HOST, never from a media query in here: the workspace docks the panel from
   * `md` up and shows the bottom sheet below that (`canDockPanel`), which is a decision
   * about the list beside it and not one this component can re-derive. It is also not a
   * question the contact SURFACE can answer — the panel is under 600px wide in both
   * presentations, so `useContactSurfaceDown('sm')` is true either way.
   */
  presentation?: "docked" | "sheet";
  /**
   * The list's OWN status/hot mutation handlers (`useCrmMutations` via the workspace) —
   * what the sheet's triage row calls. The same functions the row controls call, so the
   * optimistic row patch, the rollback toast and the `customerSeriousness` mirror all
   * come along for free; the panel deliberately owns no second write path.
   *
   * SHEET ONLY, and only when `row` is present. On a phone the open sheet COVERS the row,
   * so its status pill and 🔥 are unreachable exactly when the realtor is looking at the
   * contact — pilot feedback 2026-08-24: "we want to show the status and hot customer
   * icon on the top, for fast setting". Docked, the row is visible right beside the panel
   * and a second pair of the same controls would just be clutter. And without a `row`
   * (a cold deep link the loaded pages don't contain) there is nothing to hand
   * `useCrmMutations`, whose contract is the row it patches — the triage strip simply
   * waits for the list to find the row, like the rest of the row-fed header does.
   */
  onSetStatus?: (contact: CrmContactRowType, status: CrmStatus | null, subStatus?: string | null) => void;
  onToggleHot?: (contact: CrmContactRowType) => void;
  /**
   * MAY THE VIEWER WRITE `row`'s DOCUMENT — the list's own answer, not a second one.
   *
   * It governs the sheet's triage strip and nothing else, because the strip is the one
   * control on this panel whose subject is the ROW rather than `contact`: the rest of the
   * body is hydrated `preferOwn` and is therefore always the viewer's own copy.
   *
   * ⚠️ IT IS NOT `rowIsOwn`, and the difference is the whole reason it is a prop.
   * `useCrmPanelContact`'s `rowIsOwn` reads an ABSENT `ownerUserId` as "the viewer's own"
   * — the inference `crmRowAccess` exists to correct — so the two disagree exactly where
   * the row is unidentifiable, and the panel would then offer a control the row beside it
   * withholds. `handleSetStatus` re-tests and returns early, so that is a DEAD control
   * rather than a write: a drawer that opens, accepts a pick, closes, and silently does
   * nothing. One predicate for a row and the panel it opens.
   *
   * Defaults TRUE so the docked/legacy callers are unchanged; the strip is sheet-only
   * anyway.
   */
  canEdit?: boolean;
  /**
   * THE POOLED BOOK (plan D4): the row's "my list" standing and the two actions on it,
   * decided by the workspace (`crmPooledRowAccess`) — the same object the row's ⋮ gets.
   * It draws ONE line under the name, like the מתווך מטפל line: `ב"הלקוחות שלי" · הסר` or
   * `לא ב"הלקוחות שלי" · הוסף` — the first tab's own name (`crmViewTabTitles`), so the
   * short action next to it can only mean that list. Deliberately not a header action (the title line's
   * ~132px budget is spent; see the formula above). `null` on a personal book, where the
   * line does not exist and the panel is byte for byte what it was.
   *
   * It also decides the READ: on the office tab the panel asks under the agency view,
   * because under `mine` the server narrows the office book to the viewer's own list and a
   * record they do not hold is "not found" there.
   */
  pooled?: CrmPooledRowActions | null;
};

export default function CrmContactPanel({
  contactId,
  row,
  scopeIsOwnBook = true,
  orgId = null,
  canEdit = true,
  onClose,
  onOpenFull,
  onContactPatched,
  onContactRekeyed,
  presentation = "docked",
  onSetStatus,
  onToggleHot,
  pooled = null
}: Props) {
  const isSheet = presentation === "sheet";

  // Expanding this already-loaded contact should only change its layout. Warm the
  // full-page header while the panel is visible, without fetching other tabs' data.
  useEffect(() => {
    preloadContactDetailViewChunks();
  }, []);

  /**
   * WHO HANDLES THIS PERSON — resolved HERE rather than taken as a prop, because a COLD
   * DEEP LINK has no row in the loaded list and would otherwise render blank forever.
   *
   * It shares the module store with the list's `useCrmAssignments`, so on a warm page this
   * issues nothing at all — the list has already answered for every phone on screen.
   */
  const { handler, setLocal: setLocalHandler } = useContactHandler(contactId, { orgId: orgId ?? undefined });
  const [assignOpen, setAssignOpen] = useState(false);
  const { orgs } = useOrgDirectory();
  const { user: authUser } = useAuth();
  const canAssign = canAssignHandler(
    { inAgency: orgs.length > 0, isAdmin: orgs.some((org) => org.id === orgId && org.isAdmin) },
    handler,
    (authUser?.email as string | undefined) ?? null
  );
  const [editOpen, setEditOpen] = useState(false);
  /**
   * The panel body is its own layout context: everything inside it sizes against THIS
   * column's width, not the window's. Ten breakpoint queries live in the reused tab
   * bodies, and on a wide screen every one of them was answering about the screen.
   */
  const { surfaceRef, ContactSurfaceProvider } = useContactSurface();

  // Both the hydration read and the tab-count queries hang off this, so a row the user
  // only passed through never costs a request.
  const settledContactId = useDebounce(contactId, BODY_SETTLE_MS);
  const bodyReady = settledContactId === contactId;

  /**
   * OWN-FIRST (`preferOwn`) — org-tab feedback, 2026-08-26: "clicking a contact in the
   * org tab that is not ours should open the contact timeline side panel, not the card
   * from the סוכן מטפל; show the card only if saved for us too." So a colleague's row
   * hydrates from the VIEWER's own book (ids are the intl phone, so the copies share
   * one): our copy exists → the full own-contact panel over OUR document; it doesn't →
   * `missing` with no `get-details` fetch at all, and the body below renders the
   * timeline-only view — the person's history with US, which is what the row click was
   * asking about.
   *
   * ⚠️ `preferOwn` IS ALSO WHAT MAKES EVERY EDITOR BELOW THIS LINE SAFE, and it is the
   * only thing that does. It forces hydration through `useLiveContact` on
   * `dashboard-users-v2/{viewer}/contacts/{id}`, so `contact` is ALWAYS the viewer's own
   * document — which is why the header's "עריכה"/"ערוך פרטים" (`PanelEditDialog` →
   * `EditContactDialog`) and every inline editor inside `ContactDetailTabs` need no
   * ownership check of their own: they are all writing the viewer's record by
   * construction, and `canInlineEditContact` reads `contact.userId`, which is the
   * viewer's. Drop this flag — or add a second CRM caller of the hook without it — and
   * both re-open at once onto a colleague's `get-details` document, which saves with
   * neither an owner nor `view: "org"`: cause 3 in `crmRowAccess`, the silent
   * wrong-document write.
   */
  const { contact, loading, error, missing, isOwn, rowIsOwn } = useCrmPanelContact(
    bodyReady ? contactId : null,
    row?.ownerUserId,
    {
      preferOwn: true,
      // The office tab of a POOLED book reads under the agency view — see the `pooled` prop.
      // A held record is in the office book too, so the one scope serves the whole tab.
      readScope: pooled && !scopeIsOwnBook ? "org" : undefined,
      orgId: pooled?.orgId ?? orgId
    }
  );

  /** The timeline-only body's stub — `TimelineTab` reads only `contact.id` (the phone). */
  const timelineStub = useMemo(
    () => ({ id: contactId, callerFirstName: row?.name ?? "" } as unknown as RealEstateContact),
    [contactId, row?.name]
  );

  /**
   * Push the live document back onto the list row whenever it actually changes.
   *
   * Two guards, both load-bearing. The SERIALIZED COMPARISON is what stops a feedback
   * loop: patching the row re-renders the workspace, which re-renders this panel with a
   * new `row` object, and an unguarded effect would emit again forever. The KEY-ON-ID
   * reset is what stops the first render after switching contacts from being judged
   * against the previous contact's projection — without it, arrowing from a contact with
   * a budget to one without would skip the patch that clears it.
   */
  const lastPatchRef = useRef<{ contactId: string; json: string } | null>(null);
  useEffect(() => {
    // ⚠ OWN ROW ONLY. Under `preferOwn` the loaded document can be the VIEWER's copy of
    // a person whose ROW belongs to a colleague — projecting MY budget and status onto
    // THEIR list row would corrupt the org view in place. The two documents are
    // different records that happen to share a phone.
    if (!contact || !onContactPatched || !rowIsOwn) return;
    const patch = projectContactOntoRow(contact);
    const json = JSON.stringify(patch);
    if (lastPatchRef.current?.contactId === contactId && lastPatchRef.current.json === json) return;
    lastPatchRef.current = { contactId, json };
    onContactPatched(patch);
  }, [contact, contactId, onContactPatched, rowIsOwn]);

  // The panel always had a fallback of its own, which is why it showed the name while the
  // list beside it read "ללא שם". Same helper as the list now, so the two cannot disagree;
  // the live Firestore document stays the last resort, since it is the only source here
  // that is never a mirror.
  const displayName =
    (row ? crmRowDisplayName(row) : "") || getFullName(contact?.callerFirstName, contact?.callerLastName) || "";
  const fullName =
    displayName ||
    [contact?.callerFirstName, contact?.callerLastName].filter(Boolean).join(" ").trim();
  const phone = formatContactPhone(contactId);

  /**
   * WHO THIS PERSON IS TO ME — קונה / מוכר / שוכר / משכיר, at the top of the panel.
   *
   * Asked for directly (2026-08-25: "in the side panel we need to show the customer type
   * somewhere at the top"). It is the first thing that decides how the rest of the panel
   * should be read — a מוכר's panel does not even carry a search profile any more — so it
   * belongs with the NAME rather than down among the details.
   *
   * ── Through the shared reader, and the row is the FALLBACK, not the other way round ──
   *
   * `getContactCustomerTypes` is mandatory here: `customerTypes` is the canonical array and
   * `customerType` a legacy scalar mirror, and old documents carry ONLY the scalar — reading
   * the array alone would show a legacy contact as untyped. The live document wins when it
   * has arrived because it is authoritative and reflects an edit made in this panel
   * immediately; the row is what makes the badge appear on the SAME FRAME as the click,
   * before any document read resolves. `.length` rather than truthiness because the reader
   * returns `[]`, which is truthy.
   *
   * Joined into ONE chip rather than one chip per type: two or three adjacent pills at this
   * width fragment into an unreadable row (the same failure the mobile card's fact line hit
   * before it got a single shrinking tail), and multi-type contacts are rare.
   */
  const customerTypes = useMemo<CustomerType[]>(() => {
    const live = getContactCustomerTypes(contact as never);
    if (live.length > 0) return live;
    // The row's copy is the mirror's `customer_types` column, generated from the same two
    // document fields, so the values are the same Hebrew strings — but the wire type is
    // `string[]`. The cast is safe in the direction that matters: an unrecognised value
    // falls through `ContactTableCustomerChip`'s lookups to the raw text and the `default`
    // colour, so a bad one renders plainly rather than throwing or vanishing.
    return (row?.customerTypes ?? []) as CustomerType[];
  }, [contact, row?.customerTypes]);
  const customerTypeLabel = customerTypes.join(" · ");

  /**
   * Does the sheet's triage strip still have anything to draw once it goes read-only?
   *
   * ⚠ THAT STRIP READS AND WRITES `row`, NOT `contact`. Under own-first the body below can
   * be the VIEWER's own copy of a colleague's person while `row` stays the COLLEAGUE's
   * record — so on the agency tab those two controls reported, and wrote, a different
   * document from the panel sitting under them. Read-only keeps the colleague's status and
   * 🔥 legible (reading a teammate's pipeline is what the agency tab is for) and takes away
   * the tap that changed it.
   *
   * But read-only, both controls render NOTHING when the contact carries neither —
   * `CrmStatusControl` drops the `+ סטטוס` invitation, `CrmHotToggle` drops the off-state
   * flame — and measured across the pilot org that is 95% of contacts for status and 99.8%
   * for 🔥. So the strip has to be withheld as a whole, or nearly every foreign row leaves
   * a bare 10px band of its own top margin under the name, which reads as a control that
   * failed to load.
   */
  const triageStripHasContent = canEdit || Boolean(row?.crmStatus) || Boolean(row?.crmIsHot);

  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      {/* ── Header. Fixed: the identity and the actions a realtor came for stay
             reachable however far the tab content scrolls. ──────────────────────── */}
      <Box
        sx={{
          // NOT `position: sticky` any more. The panel now owns its own scrollport (the
          // tab PANELS scroll; everything above them does not), so the header is simply a
          // flex child that never moves — which is what sticky was approximating, without
          // the offset arithmetic and without a scroll context to get right.
          flexShrink: 0,
          bgcolor: "background.paper",
          px: 2,
          pt: 2,
          pb: 1.5
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: "50%",
              bgcolor: "primary.lighter",
              color: "primary.darker",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              fontWeight: 700
            }}
          >
            {contactInitials(fullName, contactId)}
          </Box>

          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            {/* ── THE TITLE LINE: name, then the customer-type badge ─────────────────
                The badge sits HERE and not on the line below because that line is
                already over budget. THE FORMULA, and the only place it is written out:
                the panel width, minus its 32px of padding, minus the 44px avatar, minus the
                header Stack's TWO 12px gaps (three children: avatar · identity · actions),
                minus the action cluster. Shipped at `md` that is 420 − 32 − 44 − 24 − 188
                (five 36px buttons and their four 2px gaps) = **132px** — while the phone and
                the relative time beneath totalled ~147px at their own sizes when this was
                decided (the time was removed in Trello #237; the phone alone is ~105). Line one
                carries a single short Hebrew name and is the only place with room.
                It also matches the mobile card, where the 🔥 badge was moved to sit
                directly after the name for the same reason. */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              {/* `title` because this line CAN truncate and the name is the one thing on
                  it worth recovering: at `md` a contact carrying a customer-type chip
                  leaves the name ~90px, and a long one ellipsizes. The mobile card
                  answers the same problem the same way. */}
              <Typography variant="subtitle1" noWrap title={fullName || phone} sx={{ minWidth: 0 }}>
                {/* A nameless contact (9% of the book) shows its phone as the title rather
                    than an empty heading. */}
                {fullName || (
                  <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                    {phone}
                  </Box>
                )}
              </Typography>

              {customerTypes.length > 0 && (
                <Box
                  component="span"
                  /* Every type, for the multi-type contact whose tail is a "+N". */
                  title={customerTypeLabel}
                  sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  {/* ⚠️ THE SHARED CHIP, NOT A LOCAL PILL. `customerTypeColors` in
                      `ContactTableCustomerChip` is declared the "single source of truth for
                      customer-type colors — every surface that shows a customer type reads
                      this map", and the full contact page's own header renders exactly this
                      component. A neutral grey pill here would have been a second visual
                      language for the one field a realtor identifies a contact by, on the
                      surface that is the compact twin of that header. It also brings the
                      locale with it — the label is translated, not the raw Hebrew value. */}
                  <ContactTableCustomerChip type="customerType" customerChipType={customerTypes[0]} />

                  {/* ONLY THE FIRST TYPE IS DRAWN, and the rest collapse. Docked at `md`
                      this whole line has ~132px (see the formula above): one chip leaves
                      the name ~80px, two leave it ~35px, and the name is the panel's title. "+N" is the same overflow
                      idiom as the row's avatar stack, it is bounded, and the `title` above
                      still names every type. Multi-type contacts are the rare case, so the
                      common panel is byte-for-byte what the full page shows. */}
                  {customerTypes.length > 1 && (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 0.5,
                        height: 20,
                        borderRadius: 0.75,
                        bgcolor: "background.neutral",
                        color: "text.secondary",
                        fontSize: 11,
                        fontWeight: 700,
                        lineHeight: 1
                      }}
                    >
                      {/* LTR-isolated: a bare "+2" is a bidi neutral followed by a digit and
                          lands as "2+" inside an RTL line — the same defect that printed a
                          capped total as "+2,000" on the list's count line. */}
                      <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                        {`+${customerTypes.length - 1}`}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Stack>

            {/* The phone, as its OWN LTR inline box so RTL never reorders its digits.
                The "updated N ago" stamp that sat beside it was removed on request (Trello
                #237). `updatedTime` moves on every write, system ones included, so it is not
                when anyone last dealt with the person — מגע אחרון in the body answers that. */}
            {fullName && (
              <Box
                component="span"
                dir="ltr"
                sx={{ display: "inline-block", mt: 0.25, fontSize: 13, color: "text.secondary" }}
              >
                {phone}
              </Box>
            )}
          </Box>

          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            {/* DOCKED ONLY — and `tel:` is absent from BOTH branches here, because on a
                desktop it is the one action of the five that cannot be performed. On a
                phone these two join it on the second row, as labelled targets. See the
                block above `Props`. */}
            {!isSheet && (
              <>
                <Tooltip title="ערוך פרטים">
                  {/* `span` because a disabled button fires no events, and MUI's Tooltip
                      needs one to know it is being hovered. Disabled until the document
                      is in hand: the form is seeded from it, and an edit dialog over a
                      row projection would write a mangled document — see
                      `CrmEditContactDialog`, which exists for exactly that reason. */}
                  <Box component="span" sx={{ display: "inline-flex" }}>
                    <IconButton
                      aria-label="ערוך פרטים"
                      size="small"
                      disabled={!contact}
                      onClick={() => setEditOpen(true)}
                      sx={{ width: 36, height: 36 }}
                    >
                      <EditRoundedIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                  </Box>
                </Tooltip>
                <Tooltip title="וואטסאפ">
                  <IconButton
                    aria-label="וואטסאפ"
                    size="small"
                    component="a"
                    href={whatsappHref(contactId)}
                    target="_blank"
                    rel="noopener"
                    sx={{ width: 36, height: 36, color: "#25D366" }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Tooltip>
                {/* The quick-actions menu, as the `+` the pilot asked for. Sized to its
                    neighbours; DISABLED until the document arrives is the component's own
                    rule, for the same reason the edit button above states — every item in
                    it is seeded from the document, and this header renders from the row
                    projection until the live read lands.

                    ⚠️ THIS BUTTON IS WHY `PANEL_WIDTH.md` IS 420. A fifth action costs the
                    title line 38px (36 + the 2px of `spacing={0.25}`), and at the old 380
                    that line had **130px** to begin with — so it dropped to **92**, of which
                    a customer-type chip takes ~52 and the name kept ~40. A two-word Hebrew
                    name at `subtitle1` wants ~58, so a chipped contact ellipsized its own
                    name and the line below (phone ~105px + the relative time) lost its stamp
                    to `noWrap`. Widening `md` to the value `lg` already used takes it to
                    **132** — slightly MORE than the 130 it had before the button. Add a
                    SIXTH action and this has to be redone: see the formula above, and
                    `PANEL_WIDTH` in `CrmWorkspace`. */}
                <ContactQuickActionsMenu contact={contact} variant="icon" iconSize={36} />
              </>
            )}
            {/* The full page reuses this panel's live document during the handoff. */}
            <Tooltip title="פתח בעמוד מלא">
              <IconButton
                aria-label="פתח בעמוד מלא"
                size="small"
                onClick={() => onOpenFull(contactId)}
                sx={{ width: 36, height: 36 }}
              >
                <OpenInFullIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="סגור">
              <IconButton aria-label="סגור" size="small" onClick={onClose} sx={{ width: 36, height: 36 }}>
                <CloseRoundedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ── SHEET ONLY: the triage row ──────────────────────────────────────────
            Status + 🔥 directly under the name, full-size and labelled — "for fast
            setting" (pilot, 2026-08-24). On a phone this sheet covers the list row that
            carries these same controls, so without them here reading a contact and
            triaging it were two different screens. The handlers are the LIST's own
            (see the props), so a change repaints the row behind the sheet optimistically
            and rolls back with the same toast on failure. The pill keeps the DEFAULT
            `subStatus="append"` — `בטיפול · בהצגת נכסים` — because unlike the card this
            line has the width for both halves. The card shows the refinement INSTEAD of
            its parent (`"replace"`), so between them every mobile surface now names the
            sub-status somewhere outside the picker drawer.

            READ-ONLY OVER A COLLEAGUE'S ROW, and withheld entirely when that leaves it with
            nothing to draw — see `triageStripHasContent`. The strip's subject is `row`,
            which on the agency tab is somebody else's document. */}
        {/* BOTH presentations since 2026-09-30. It was sheet-only, so the docked panel — which is
            what opens over a listing's matched buyers — showed the status nowhere and offered no way
            to change it, while /crm's table let you set it inline. */}
        {row && onSetStatus && onToggleHot && triageStripHasContent && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }}>
            <CrmStatusControl
              contact={row}
              minTouchTarget={44}
              onSetStatus={(status, sub) => onSetStatus(row, status, sub)}
              onToggleHot={() => onToggleHot(row)}
              readOnly={!canEdit}
            />
            <CrmHotToggle
              isHot={row.crmIsHot}
              onToggle={() => onToggleHot(row)}
              size={44}
              readOnly={!canEdit}
            />
          </Stack>
        )}

        {/* ── SHEET ONLY: the second row ──────────────────────────────────────────
            The three things you DO to the contact, as full-width labelled 44px targets.
            Labelled rather than iconic because one of them is NEW: an unfamiliar pencil
            among four other glyphs is not discoverable, and there is room for words here
            precisely because they are no longer competing with the name for one line. */}
        {isSheet && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 1.25 }}>
            <Button
              size="small"
              color="inherit"
              component="a"
              href={telHref(contactId)}
              startIcon={<PhoneRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ flex: 1, minWidth: 0, minHeight: 44, fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}
            >
              התקשר
            </Button>
            <Button
              size="small"
              component="a"
              href={whatsappHref(contactId)}
              target="_blank"
              rel="noopener"
              startIcon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
              sx={{ flex: 1, minWidth: 0, minHeight: 44, fontWeight: 700, fontSize: 13, whiteSpace: "nowrap", color: "#25D366" }}
            >
              וואטסאפ
            </Button>
            <Button
              size="small"
              color="inherit"
              disabled={!contact}
              onClick={() => setEditOpen(true)}
              startIcon={<EditRoundedIcon sx={{ fontSize: 18 }} />}
              sx={{ flex: 1, minWidth: 0, minHeight: 44, fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}
            >
              עריכה
            </Button>
            {/* The fourth action, and the only unlabelled one — see the `+` paragraph above
                `Props` for the width arithmetic that made it icon-only. `flexShrink: 0` so
                it keeps its 44px touch target while the three labels absorb the loss. */}
            <ContactQuickActionsMenu contact={contact} variant="icon" iconSize={44} />
          </Stack>
        )}

        {/* Whose record is ON SCREEN. Under own-first, a colleague's row served from
            the viewer's own copy must SAY so — the row two inches away belongs to the
            colleague, and a panel that silently shows different numbers than the row it
            opened from reads as a sync bug. Absent on the `mine` view, which would say
            the same thing on all 50 rows. */}
        {contact && isOwn && !rowIsOwn && row?.ownerUserId && (
          <Typography variant="caption" noWrap sx={{ display: "block", mt: 1, color: "text.secondary" }}>
            {`מוצג הכרטיס שלך · שמור גם אצל ${row.ownerName || row.ownerUserId}`}
          </Typography>
        )}

        {/* ── WHO HANDLES THIS PERSON, AND THE CONTROL THAT SETS IT ──────────────────
            ⚠️ DELIBERATELY NOT A SIXTH HEADER ACTION. The title line's action cluster has a
            documented ~132px budget at `md`, and this file's own header warns in capitals
            that a sixth control there forces that arithmetic to be redone. This line is
            free, sits directly under the name, and is the natural place for a fact ABOUT
            the person rather than an action ON them.

            It renders on BOTH tabs and above the body — which is what the pilot asked for
            ("a button on the top, above the timeline"): on a colleague's contact the viewer
            does not hold, the body below IS a timeline and nothing else, so this row is the
            only place the designation can be read or changed at all.

            The button is WITHHELD, never disabled, when the caller may not set it; when
            nobody is designated and the caller may not set it either, the line explains why
            instead of showing an empty seat — there is room for a sentence here, unlike a
            60px table cell. */}
        {(handler !== undefined || canAssign) && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, minWidth: 0 }}>
            <CrmHandlerBadge handler={handler} size="xs" variant="chip" reserve={false} />
            {canAssign && (
              <Button size="small" variant="text" onClick={() => setAssignOpen(true)} sx={{ fontWeight: 700, flexShrink: 0 }}>
                {handler ? "העברת טיפול" : "שיוך מתווך מטפל"}
              </Button>
            )}
            {!canAssign && handler === null && (
              <Typography variant="caption" noWrap sx={{ minWidth: 0, color: "text.disabled" }}>
                {`אין מתווך מטפל · ${CRM_HANDLER_ADMIN_ONLY_HINT}`}
              </Typography>
            )}
          </Stack>
        )}

        {/* ── THE POOLED BOOK: "MY LIST" ──────────────────────────────────────────────
            One line, same shape as the handler line above it: a fact about the person
            ("on your list" / "not on your list") and the one action that changes it. The
            office's DELETE is deliberately not here — it is office-wide, red and confirmed,
            and lives behind the row's ⋮. While the switch runs the actions are gone and
            the line says why, in the server's own words. */}
        {pooled && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1, minWidth: 0 }}>
            {pooled.busy ? (
              <Typography variant="caption" noWrap sx={{ minWidth: 0, color: "text.disabled" }}>
                {POOLED_BOOK_BUSY_MESSAGE}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" noWrap sx={{ minWidth: 0, color: "text.secondary" }}>
                  {pooled.access.held ? `ב"${POOLED_TAB_TITLES.mine}"` : `לא ב"${POOLED_TAB_TITLES.mine}"`}
                </Typography>
                {pooled.access.canRemoveFromMyList && (
                  <Button size="small" variant="text" onClick={pooled.onRemoveFromMyList} sx={{ fontWeight: 700, flexShrink: 0 }}>
                    הסר
                  </Button>
                )}
                {pooled.access.canAddToMyList && (
                  <Button size="small" variant="text" onClick={pooled.onAddToMyList} sx={{ fontWeight: 700, flexShrink: 0 }}>
                    הוסף
                  </Button>
                )}
              </>
            )}
          </Stack>
        )}

        {assignOpen && orgId && (
          <CrmAssignHandlerDialog
            open
            orgId={orgId}
            contactId={contactId}
            displayName={contact?.contactFullName ?? row?.name ?? null}
            handler={handler ?? null}
            onClose={() => setAssignOpen(false)}
            onAssigned={setLocalHandler}
          />
        )}
      </Box>
      <Divider />

      {/* ── Body ─────────────────────────────────────────────────────────────────── */}
      {/* `surfaceRef` measures THIS box, and everything rendered inside it lays itself
          out against that width instead of the window's. `compact` below only ever
          governed the tab STRIP; the ten breakpoint queries in the tab BODIES still
          answered about the 1920 screen the panel happens to sit on, which is what made
          the content read as though it continued past the panel's edge. See
          `contacts/detail/contactSurface.tsx`. */}
      {/* ⚠️ THIS BOX IS THE PANEL'S LAYOUT SPINE, and it does not scroll.
             It is a flex COLUMN filling what the header left, holding two children: the
             tab strip (fixed) and the panels (scrolling). The host — the docked Card or
             the bottom Drawer — is `overflow: hidden` with a definite height, so this is
             the only scrollport in the panel and there is exactly one thing that moves. */}
      <Box
        ref={surfaceRef}
        sx={{ flexGrow: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {bodyReady && !loading && !error && contact ? (
          <ContactSurfaceProvider>
            <ContactDetailTabs
              /* The body — not the panel — is what carries per-contact state (the open tab
                 and its count queries), so this is where the remount belongs. */
              key={contactId}
              contact={contact}
              /* Forces the narrow layout. Without it the strip and every panel read the
                 VIEWPORT and lay themselves out for the 1440px screen they are technically
                 on, inside a 420px column. */
              compact
              /**
               * THE TABS STAY PUT — reported as "keep the top tabs fixed, along with the
               * header (currently they are hidden when we scroll)".
               *
               * The strip used to be the first thing inside the scrolling body, so it left
               * with the content and a realtor reading down a timeline had no way back to
               * another tab without scrolling to the top first. Taking the two elements
               * separately lets the strip be a fixed flex child beside the header while
               * only the panels scroll — which is better than making the strip sticky,
               * because there is then no header height to measure and nothing to keep in
               * sync when the header grows a second row.
               */
              renderLayout={(strip, panels) => (
                <>
                  <Box sx={{ flexShrink: 0, px: { xs: 1, md: 1.5 } }}>{strip}</Box>
                  <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", px: { xs: 1, md: 1.5 }, pb: 1.5 }}>
                    {panels}
                  </Box>
                </>
              )}
            />
          </ContactSurfaceProvider>
        ) : bodyReady && !loading && !error && missing && !rowIsOwn && row ? (
          /* ── TIMELINE-ONLY: a colleague's contact the viewer has NOT saved ─────────
             The colleague's document is deliberately not fetched (see `preferOwn`), so
             there is no card to show — what there IS is the person's history with THIS
             viewer, which `TimelineSurface` answers from the viewer's own phone-keyed
             data (calls, messages, meetings) plus the agency's שמור-אצל block where
             permitted. The header above still names and reaches the person from the
             row projection. */
          <ContactSurfaceProvider>
            <Alert severity="info" sx={{ mx: 1.5, mt: 1.5, flexShrink: 0 }}>
              {`הלקוח שמור אצל ${row.ownerName || row.ownerUserId} ולא אצלך — מוצג ציר הזמן שלך איתו.`}
            </Alert>
            <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", px: { xs: 1, md: 1.5 }, py: 1.5 }}>
              {/* What the office's agents sent this customer (their minisites) — the manager's
                  view of a customer they do not hold. Renders nothing for anyone without that
                  access, or when nobody sent anything. */}
              <Box sx={{ mb: 2, "&:empty": { display: "none" } }}>
                <OfficeMinisitesPanel contactId={contactId} standalone />
              </Box>
              <TimelineTab contact={timelineStub} />
            </Box>
          </ContactSurfaceProvider>
        ) : (
          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: "auto", px: { xs: 1, md: 1.5 }, py: 1.5 }}>
            {(!bodyReady || loading) && <PanelBodySkeleton />}

            {bodyReady && !loading && error && (
              <Alert severity="error" sx={{ mx: 0.5 }}>
                לא הצלחנו לטעון את פרטי הלקוח.
              </Alert>
            )}

            {bodyReady && !loading && !error && missing && (
              <Alert severity="info" sx={{ mx: 0.5 }}>
                {/* ⚠ "ייתכן שנמחק" REQUIRES POSITIVE EVIDENCE THAT THE RECORD WAS THE
                    VIEWER'S. Neither input says so on its own. `missing` is a live read of
                    `dashboard-users-v2/{viewer}/contacts/{id}` — it reports that the viewer
                    has no copy, never whose the row was — and `rowIsOwn` reads an ABSENT
                    `ownerUserId` as "mine" (`useCrmPanelContact.ts:110`; that default is
                    right for its other callers and is not being changed). `row` goes null
                    whenever the open contact leaves the loaded page: a filter, a search, a
                    sort, paging past it, or a cold `?view=org&contact=X` deep link. So on
                    the agency tab those two conspired, and a contact the viewer had never
                    owned was announced as DELETED.

                    On the caller's own book the scope itself is the evidence — the server
                    returns only `visibleOwnerIds: [userId]` — so a document missing there
                    genuinely is a deletion. On the agency book with no row we do not know
                    whose it is, and "yours, and probably deleted" is the one answer that is
                    both wrong and alarming. */}
                {scopeIsOwnBook || (rowIsOwn && row)
                  ? "הלקוח לא נמצא. ייתכן שנמחק."
                  : "לא ניתן להציג את הרשומה המלאה של הלקוח הזה."}
              </Alert>
            )}
          </Box>
        )}
      </Box>

      {/* Mounted on demand — `usePropertyTypes` inside it costs a facet request, which a
          panel that is merely OPEN has no reason to pay. */}
      {editOpen && contact && (
        <PanelEditDialog
          contact={contact}
          onClose={() => setEditOpen(false)}
          /* Same guard as the projection effect: under own-first the open document can
             be the viewer's copy of a colleague's row, and its save/re-key must never
             be written onto that row. The panel's own live listener still reflects the
             edit inside the panel. */
          onSaved={rowIsOwn ? onContactPatched : undefined}
          onRekeyed={rowIsOwn ? onContactRekeyed : undefined}
        />
      )}
    </Stack>
  );
}

/**
 * The app's own edit dialog, over the document THIS PANEL ALREADY HOLDS.
 *
 * ⚠️ Deliberately NOT `CrmEditContactDialog`, which the row menu uses. That component
 * exists to hydrate a contact from a row PROJECTION before it can open a form, and the
 * panel has already done exactly that hydration — mounting it here would open a second
 * read (a duplicate Firestore listener for the caller's own contact, a second
 * `get-details` round trip for a colleague's) to fetch a document sitting in a variable
 * one component up. The reasoning in its header about why a row must never be passed
 * straight to `EditContactDialog` still applies in full; it is simply already satisfied.
 *
 * `onSaved` is belt to the live listener's braces. An own contact's edit comes back
 * through `useCrmPanelContact` and patches the row on its own, but a COLLEAGUE's is read
 * through `get-details`, which is a one-shot — without this the row beside an open panel
 * would keep the old values after a save, which is indistinguishable from a save that
 * failed. The panel's patch effect dedupes on the serialized patch, so the two paths
 * agreeing costs nothing.
 */
function PanelEditDialog({
  contact,
  onClose,
  onSaved,
  onRekeyed
}: {
  contact: RealEstateContact;
  onClose: () => void;
  onSaved?: (patch: Partial<CrmContactRowType>) => void;
  onRekeyed?: (
    oldContactId: string,
    savedRow: Partial<CrmContactRowType> & { contactId: string }
  ) => void;
}) {
  const { propertyTypes } = usePropertyTypes();
  return (
    <EditContactDialog
      open
      contact={contact}
      propertyTypes={propertyTypes}
      onClose={onClose}
      /* The payload is the whole hydrated document with the edit applied, which is what
         makes projecting it wholesale safe. Three values in it are one step behind and all
         three are server-derived — see `CrmEditContactDialog` for the list. No `onClose`
         here: the dialog closes itself before calling this. */
      onSuccess={(saved) => {
        if (saved) onSaved?.(projectContactOntoRow(saved as RealEstateContact));
      }}
      /* A re-key leaves the document's CONTENT untouched — only its identity moved — so
         the row patch is the same projection `onSuccess` uses, with the NEW id attached
         explicitly (the projection deliberately returns no `contactId`). Mirrors
         `CrmEditContactDialog.onPhoneChanged`; the panel itself follows the re-pointed
         `?contact=` on its own. */
      onPhoneChanged={(oldContactId, result) => {
        onRekeyed?.(oldContactId, {
          ...projectContactOntoRow(contact),
          contactId: result.newContactId
        });
      }}
    />
  );
}

/** Mirrors the tab strip + first panel, so the body does not jump when the contact lands. */
function PanelBodySkeleton() {
  return (
    <Box sx={{ px: 1 }}>
      <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, mb: 2 }} />
      <Skeleton variant="text" width="55%" height={26} />
      <Skeleton variant="text" width="35%" />
      <Skeleton variant="rectangular" height={92} sx={{ borderRadius: 1, mt: 2 }} />
      <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1, mt: 1.5 }} />
    </Box>
  );
}
